import { format } from "date-fns";
import { Pencil, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatCurrency } from "@/lib/formatters";
import { cn } from "@/lib/utils";

import type { BudgetOverview, BudgetRule } from "@/schemas";

import { categoryLabel } from "../lib/budget-helpers";
import { occurrenceStatusClasses } from "../types";

import { EmptyStateCard } from "./empty-state-card";

export function BudgetOccurrencesCard({
  currentMonthLabel,
  overview,
  rules,
  currencyCode,
  onEditRule,
  onDeleteRule,
}: {
  currentMonthLabel: string;
  overview: BudgetOverview | null;
  rules: BudgetRule[];
  currencyCode: string;
  onEditRule: (rule: BudgetRule) => void;
  onDeleteRule: (ruleId: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity for {currentMonthLabel}</CardTitle>
        <CardDescription>
          Scheduled occurrences for the current month.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {overview?.occurrences?.length ? (
          overview.occurrences.map((occurrence) => {
            const rule = rules.find((item) => item.id === occurrence.rule_id);

            return (
              <div
                key={`${occurrence.rule_id}-${occurrence.occurrence_date}`}
                className="flex flex-col gap-3 rounded-lg border p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {occurrence.label}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(occurrence.occurrence_date), "MMM d")} •{" "}
                      {categoryLabel(occurrence.category)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {formatCurrency(occurrence.amount, currencyCode)}
                    </p>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "mt-1.5 font-normal",
                        occurrenceStatusClasses[occurrence.status],
                      )}
                    >
                      {occurrence.status.replace(/_/g, " ")}
                    </Badge>
                  </div>
                </div>

                {rule && (
                  <div className="flex items-center gap-2 border-t pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => onEditRule(rule)}
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-destructive hover:text-destructive"
                      onClick={() => void onDeleteRule(rule.id)}
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <EmptyStateCard text="No scheduled activity." />
        )}
      </CardContent>
    </Card>
  );
}
